import styled from "@emotion/styled"
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { Desktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  getNonUmbracoUrl,
  Link,
  replaceRichTextLinks,
  type AccordionItem,
} from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { StyledAccordion } from "../../Common/FaqItem"

const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    padding: 24px !important;
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  &.MuiAccordionDetails-root {
    padding: 24px 16px !important;
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding: 24px !important;
    }
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const IconTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: unset;
  }
`

const ImportantBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 675px;
  border-radius: 12px;
  background: rgba(27, 189, 119, 0.08);
  padding: 16px;
`

const DetailsFlex = styled.div<{ isSmall?: boolean }>`
  display: flex;

  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: ${({ isSmall }) => (isSmall ? "24px" : "48px")};
    align-items: center;
    flex-direction: row;
  }
`

const IconLink = styled(Link)`
  text-decoration: none;
`

const VerticalDivider = styled.div<{ isSmall?: boolean }>`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    width: 1px;
    height: ${({ isSmall }) => (isSmall ? "49px" : "60px")};
    background: ${sharedColors.borderGray};
  }
`

const IconText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`
const IconBox = styled.div`
  padding: 8px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.brandDark};
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconLinkBig = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  p {
    width: max-content;
  }
`

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
  }
`
export default function AccordionItemComp({
  item,
  index,
  isExpanded,
  onToggle,
  hideButton = false,
}: {
  item: AccordionItem
  index: number
  isExpanded: boolean
  onToggle: () => void
  hideButton?: boolean
}) {
  return (
    <StyledAccordion onChange={onToggle} expanded={isExpanded}>
      <StyledAccordionSummary
        expandIcon={
          isExpanded ? (
            <Image
              alt=""
              width={24}
              height={24}
              src={"/icons/accordionClose.svg"}
            />
          ) : (
            <Image
              alt=""
              width={24}
              height={24}
              src={"/icons/accordionOpen.svg"}
            />
          )
        }
      >
        <Typography fontWeight={700} lineHeight={"140%"}>
          {item.itemTitle}
        </Typography>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <ContentLayout>
          <FlexColumn>
            {item.openingHoursTitle && (
              <IconTitle>
                {item.openingHoursIcon && (
                  <Image
                    alt=""
                    width={20}
                    height={20}
                    src={getUmbracoMediaUrl(item.openingHoursIcon) || ""}
                  />
                )}
                <Typography variant="body1" component={"h4"} fontWeight={700}>
                  {item.openingHoursTitle}
                </Typography>
              </IconTitle>
            )}
            {item.openingHoursContent.length > 0 && (
              <FlexColumn>
                {item.openingHoursContent.map((el, i) => (
                  <FlexRow key={i}>
                    <Typography minWidth={100} variant="body2">
                      {el.day}
                    </Typography>
                    <Typography variant="body2">{el.hour}</Typography>
                  </FlexRow>
                ))}
              </FlexColumn>
            )}

            {item.importantToKnowTitle && (
              <ImportantBox>
                <Typography variant="body1" component={"h4"} fontWeight={700}>
                  {item.importantToKnowTitle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    "& p": {
                      margin: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(
                      item.importantToKnowContent || "",
                    ),
                  }}
                />
              </ImportantBox>
            )}
          </FlexColumn>
          {item.addressTitle || item.faxTitle ? (
            <DetailsFlex>
              <FlexColumn>
                {item.addressTitle && (
                  <IconTitle>
                    {item.addressIcon && (
                      <Image
                        alt=""
                        width={20}
                        height={20}
                        src={getUmbracoMediaUrl(item.addressIcon) || ""}
                      />
                    )}
                    <Typography
                      variant="body1"
                      component={"h4"}
                      fontWeight={700}
                    >
                      {item.addressTitle}
                    </Typography>
                  </IconTitle>
                )}
                {item.address && (
                  <Typography variant="body2">{item.address}</Typography>
                )}
              </FlexColumn>

              {(item.faxIcon || item.faxNumber) && <VerticalDivider />}

              <FlexColumn>
                {item.faxTitle && (
                  <IconTitle>
                    {item.faxIcon && (
                      <Image
                        alt=""
                        width={20}
                        height={20}
                        src={getUmbracoMediaUrl(item.faxIcon) || ""}
                      />
                    )}
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      component={"h4"}
                    >
                      {item.faxTitle}
                    </Typography>
                  </IconTitle>
                )}
                {item.faxNumber && (
                  <Typography variant="body2">{item.faxNumber}</Typography>
                )}
              </FlexColumn>
            </DetailsFlex>
          ) : null}

          <FlexColumn>
            {item.documentsIcon && item.documentsTitle && (
              <IconTitle>
                {item.documentsIcon && (
                  <Image
                    alt=""
                    width={20}
                    height={20}
                    src={getUmbracoMediaUrl(item.documentsIcon) || ""}
                  />
                )}
                <Typography variant="body1" fontWeight={500} component={"h4"}>
                  {item.documentsTitle}
                </Typography>
              </IconTitle>
            )}
            <FlexColumn>
              {item.documents.map((el, i) => (
                <FlexRow key={i}>
                  <Typography variant="body2" minWidth={150}>
                    {el.name}
                  </Typography>
                  <IconLink href={getUmbracoMediaUrl(el?.url)} target="_blank">
                    <Image
                      alt=""
                      width={24}
                      height={24}
                      src={"/icons/download.svg"}
                    />
                  </IconLink>
                </FlexRow>
              ))}
            </FlexColumn>
          </FlexColumn>
          <BottomContainer>
            <DetailsFlex isSmall>
              {item.phoneNumber[0]?.url && (
                <IconText>
                  <IconLinkBig href={item.phoneNumber[0]?.url || ""}>
                    <IconBox>
                      <Image
                        alt=""
                        width={24}
                        height={24}
                        src={"/icons/phone.svg"}
                      />
                    </IconBox>
                    <Typography variant="body2">
                      {item.phoneNumber[0]?.name}
                    </Typography>
                  </IconLinkBig>
                </IconText>
              )}
              {item.emailAddress[0]?.url && (
                <Desktop>
                  <VerticalDivider isSmall />
                </Desktop>
              )}
              {item.emailAddress[0]?.url && (
                <IconText>
                  <IconLinkBig href={item.emailAddress[0]?.url || ""}>
                    <IconBox>
                      <Image
                        alt=""
                        width={24}
                        height={24}
                        src={"/icons/email.svg"}
                      />
                    </IconBox>
                    <Typography variant="body2">
                      {item.emailAddress[0]?.name}
                    </Typography>
                  </IconLinkBig>
                </IconText>
              )}
            </DetailsFlex>
            {!hideButton &&
            item?.button?.url &&
            item?.button?.url?.length > 0 ? (
              <StyledButton
                variant="secondary"
                LinkComponent={Link}
                href={getNonUmbracoUrl(item.button?.url)}
              >
                {item.button.name}
              </StyledButton>
            ) : null}
          </BottomContainer>
        </ContentLayout>
      </StyledAccordionDetails>
    </StyledAccordion>
  )
}
